import { ChevronRight } from 'lucide-react'
import { IOSSwitch } from './ios-switch.jsx'

/**
 * Shared settings UI components — iOS-Settings-Style.
 * Used by Profil-Tab, Global Settings, Flea Settings, Store Settings.
 * All styling via Boothly CSS variables (no theme object, no dark prop needed).
 */

export function SectionLabel({ children }) {
  return (
    <div className="text-[11px] font-medium text-[var(--label-secondary)] tracking-wider uppercase mb-2 mt-1 px-1">
      {children}
    </div>
  )
}

export function SettingsGroup({ children }) {
  return (
    <div className="bg-[var(--bg-L1)] border border-[var(--border-default)] rounded-2xl overflow-hidden mb-5" style={{ color: 'var(--label-primary)' }}>
      {children}
    </div>
  )
}

export function SettingsRow({ icon, label, sub, onClick, trailing, destructive, testId }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left ${onClick ? 'active:opacity-80' : ''} transition-opacity`}
    >
      {icon && <span className="text-[var(--label-secondary)] flex-shrink-0">{icon}</span>}
      <div className="flex-1 min-w-0">
        <div
          className="text-[15px]"
          style={destructive ? { color: 'var(--accent-red)' } : undefined}
        >
          {label}
        </div>
        {sub && <div className="text-[12px] text-[var(--label-tertiary)] mt-0.5">{sub}</div>}
      </div>
      {trailing}
      {onClick && !trailing && !destructive && (
        <ChevronRight size={16} className="text-[var(--label-tertiary)] flex-shrink-0" />
      )}
    </Tag>
  )
}

export function SettingsToggleRow({ icon, label, sub, checked, onChange }) {
  return (
    <div className="w-full flex items-center gap-3 px-4 py-3">
      {icon && <span className="text-[var(--label-secondary)] flex-shrink-0">{icon}</span>}
      <div className="flex-1 min-w-0">
        <div className="text-[15px]">{label}</div>
        {sub && <div className="text-[12px] text-[var(--label-tertiary)] mt-0.5">{sub}</div>}
      </div>
      <IOSSwitch checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

export function SettingsValueRow({ icon, label, value, onClick, testId }) {
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left ${onClick ? 'active:opacity-80' : ''} transition-opacity`}
    >
      {icon && <span className="text-[var(--label-secondary)] flex-shrink-0">{icon}</span>}
      <span className="flex-1 text-[15px]">{label}</span>
      <span className="text-[14px] text-[var(--label-tertiary)] flex-shrink-0 tabular-nums">{value}</span>
      {onClick && <ChevronRight size={16} className="text-[var(--label-tertiary)] flex-shrink-0" />}
    </Tag>
  )
}

export function SettingsSegmentedRow({ icon, label, sub, options, value, onChange }) {
  return (
    <div className="w-full flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
      {icon && <span className="text-[var(--label-secondary)] flex-shrink-0">{icon}</span>}
      {/* Label bricht nie ab: auf schmalen Geräten rutscht das Segment in die nächste Zeile */}
      <div className="flex-1 min-w-[9rem]">
        <div className="text-[15px]">{label}</div>
        {sub && <div className="text-[12px] text-[var(--label-tertiary)] mt-0.5">{sub}</div>}
      </div>
      <div className="flex flex-shrink-0 rounded-[10px] p-0.5" style={{ background: 'var(--fill-tertiary)' }}>
        {options.map(opt => (
          <button
            key={opt.v}
            onClick={(e) => { e.stopPropagation(); onChange(opt.v) }}
            className="h-11 min-w-[44px] rounded-[8px] px-2.5 text-[13px]"
            style={{
              fontWeight: value === opt.v ? 600 : 500,
              color: value === opt.v ? 'var(--label-primary)' : 'var(--label-secondary)',
              background: value === opt.v ? 'var(--segment-selected)' : 'transparent',
              boxShadow: value === opt.v ? 'var(--shadow-sm)' : 'none',
              transition: 'background-color 150ms var(--ease-out-ios), color 150ms',
            }}
          >
            {opt.l}
          </button>
        ))}
      </div>
    </div>
  )
}

export function SettingsDivider() {
  return <div className="mx-4 h-px" style={{ background: 'var(--separator)' }} />
}
